// Reports → Kitchen flow (Stage 3): the owner's view of the store ↔ kitchen
// hand-offs. Only rendered with kitchen_flow on. Money sections (kitchen
// check, transit, stock value, buying) are for admin and the accountant; the
// manager sees requests and cooking yield — the same split as the routes —
// and, read only, the stock counts (Batch 6).
import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis } from 'recharts'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useCurrency } from '@/contexts/CurrencyContext'
import type { UseReportRange } from '@/hooks/useReportRange'
import { chartAxis, chartTooltipBar } from '@/lib/chartTheme'
import { getStoredUserRole } from '@/lib/staff-roles'
import { cn } from '@/lib/utils'
import { FR } from '@/lib/inventoryFlow/flowReportStrings'
import { CountsReadOnly } from '@/components/inventory-flow/CountsReadOnly'
import { avtChartRows, avtHeadline, avtOtherNet, avtVerdict, flowReportSections, fmtPct, fmtQty, formatMinutes } from '@/lib/inventoryFlow/reportLogic'
import type { AvTReport, FillRateRow, YieldRow } from '@/lib/inventoryFlow/visibilityTypes'

type Section = 'avt' | 'requests' | 'transit' | 'cooking' | 'stock' | 'buying' | 'counts'

export function KitchenFlowReportsTab({ range }: { range: UseReportRange }) {
  const role = getStoredUserRole()
  const sections = flowReportSections(role) as Section[]
  const [section, setSection] = useState<Section>(sections[0] ?? 'requests')
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(false)
  if (sections.length === 0) return null
  // The flow sheets, money included: admin and the accountant (the route's split).
  const canExport = role === 'admin' || role === 'accountant'
  const download = async () => {
    setExportError(false)
    setExporting(true)
    try {
      await apiClient.exportFlowReportsXlsx(range.fromISO, range.toISO)
    } catch {
      setExportError(true)
    } finally {
      setExporting(false)
    }
  }
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit max-w-full flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="tablist">
          {sections.map((s) => (
            <button key={s} type="button" role="tab" aria-selected={section === s} onClick={() => setSection(s)}
              className={cn('flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors', section === s ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
              {FR.sections[s]}
            </button>
          ))}
        </div>
        {canExport && (
          <div className="flex items-center gap-2">
            {exportError && <span className="text-sm text-danger-fg">{FR.exportFailed}</span>}
            <Button variant="outline" size="sm" disabled={exporting} onClick={() => void download()}>
              {exporting ? FR.exporting : FR.exportExcel}
            </Button>
          </div>
        )}
      </div>
      {section === 'avt' && <AvTSection range={range} />}
      {section === 'requests' && <RequestsSection range={range} />}
      {section === 'transit' && <TransitSection range={range} />}
      {section === 'cooking' && <CookingSection range={range} />}
      {section === 'stock' && <StockSection />}
      {section === 'buying' && <BuyingSection range={range} />}
      {section === 'counts' && <CountsReadOnly />}
    </div>
  )
}

function State({ loading, error, empty }: { loading: boolean; error: boolean; empty: boolean }) {
  if (loading) return <p className="text-muted-foreground">{FR.loading}</p>
  if (error) return <p className="text-danger-fg">{FR.failed}</p>
  if (empty) return <p className="text-muted-foreground">{FR.empty}</p>
  return null
}

function Table({ cols, children, className }: { cols: string[]; children: ReactNode; className?: string }) {
  return (
    <div className={cn('overflow-x-auto rounded-lg border', className)}>
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>{cols.map((c, i) => <th key={i} className={cn('px-3 py-2 font-semibold', i > 0 && 'text-right')}>{c}</th>)}</tr>
        </thead>
        <tbody className="divide-y">{children}</tbody>
      </table>
    </div>
  )
}

const td = 'px-3 py-2 text-right tabular-nums'

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'bad' | 'good' }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={cn('text-2xl font-bold tabular-nums', tone === 'bad' && 'text-danger-fg', tone === 'good' && 'text-success-fg')}>{value}</p>
      {sub && <p className="text-sm text-muted-foreground">{sub}</p>}
    </div>
  )
}

// ── Kitchen check (AvT) ─────────────────────────────────────────────────────

function AvTSection({ range }: { range: UseReportRange }) {
  const { data: locRes } = useQuery({ queryKey: ['inventory-flow', 'locations'], queryFn: () => apiClient.getFlowLocations() })
  const kitchens = (locRes?.data ?? []).filter((l) => l.is_active && l.kind !== 'transit')
  const [loc, setLoc] = useState<string | undefined>(undefined)
  const q = useQuery({
    queryKey: ['reports-v2', 'kitchen-flow', 'avt', range.fromISO, range.toISO, loc ?? ''],
    queryFn: () => apiClient.getFlowAvT(range.fromISO, range.toISO, loc),
    staleTime: 30_000,
  })
  const r = q.data?.data
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{FR.avt.title}</h2>
        <p className="text-sm text-muted-foreground">{FR.avt.explain}</p>
      </div>
      {kitchens.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{FR.avt.place}</span>
          {kitchens.map((k) => {
            const active = (loc ?? r?.location.id) === k.id
            return (
              <button key={k.id} type="button" onClick={() => setLoc(k.id)}
                className={cn('h-10 rounded-lg border px-4 text-sm font-semibold', active ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:bg-muted')}>
                {k.name}
              </button>
            )
          })}
        </div>
      )}
      {r && !r.waste_tracked && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">{FR.avt.wasteNotRecorded}</p>
      )}
      <State loading={q.isLoading} error={q.isError} empty={false} />
      {r && <AvTBody r={r} />}
    </div>
  )
}

function AvTBody({ r }: { r: AvTReport }) {
  const { formatCurrency } = useCurrency()
  const head = avtHeadline(r, formatCurrency)
  const chart = avtChartRows(r.rows)
  const gaps = r.dishes_without_recipe_count > 0 || r.events_not_wrapped_up.length > 0
  return (
    <>
      <div className={cn('rounded-lg border p-4', head.tone === 'bad' ? 'border-danger-fg/40 bg-danger-surface' : 'bg-card')}>
        <p className={cn('text-base font-semibold', head.tone === 'bad' && 'text-danger-fg')}>{head.text}</p>
        {r.items_missing_counts > 0 && r.items_with_counts > 0 && <p className="mt-1 text-sm text-muted-foreground">{FR.avt.howToCount}</p>}
      </div>
      {gaps && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          {r.dishes_without_recipe_count > 0 && (
            <div>
              <p className="font-medium">{FR.avt.noRecipe(r.dishes_without_recipe_count)}</p>
              <p>{r.dishes_without_recipe.join(', ')}{r.dishes_without_recipe_count > r.dishes_without_recipe.length ? '…' : ''}</p>
              <p>{FR.avt.noRecipeFix}</p>
            </div>
          )}
          {r.events_not_wrapped_up.length > 0 && (
            <div>
              <p className="font-medium">{FR.avt.notWrapped(r.events_not_wrapped_up.length)}</p>
              <p>{r.events_not_wrapped_up.map((e) => [e.plan_no, e.title].filter(Boolean).join(' ')).join(', ')}</p>
              <p>{FR.avt.notWrappedFix}</p>
            </div>
          )}
        </div>
      )}
      {r.items_with_counts > 0 && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label={FR.avt.missingTotal} value={formatCurrency(r.missing_value)}
            sub={r.missing_pct != null ? FR.avt.ofUsage(String(r.missing_pct)) : undefined} tone={r.missing_value > 0.005 ? 'bad' : undefined} />
          <Stat label={FR.avt.extraTotal} value={formatCurrency(r.extra_value)} sub={r.extra_value > 0.005 ? FR.avt.extraHint : undefined} />
          <Stat label={FR.avt.withCounts} value={String(r.items_with_counts)}
            sub={r.items_missing_counts > 0 ? `${FR.avt.missingCounts}: ${r.items_missing_counts}` : undefined} />
        </div>
      )}
      {r.rows.length > 0 && (
        <details className="rounded-lg border bg-card">
          <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">{FR.avt.seeAll}</summary>
          <div className="space-y-4 p-3">
            {chart.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">{FR.avt.chart}</CardTitle></CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chart} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
                      <CartesianGrid stroke="hsl(var(--border))" horizontal={false} />
                      <XAxis type="number" {...chartAxis} tickFormatter={(v: number) => formatCurrency(v)} />
                      <YAxis dataKey="name" type="category" {...chartAxis} width={140} />
                      <ReTooltip {...chartTooltipBar} formatter={(v: number) => [formatCurrency(v), v > 0 ? FR.avt.missing : FR.avt.extra]} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {chart.map((c) => <Cell key={c.name} fill={c.value > 0 ? '#dc2626' : '#0ea5e9'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            <Table cols={FR.avt.cols}>
              {r.rows.map((x) => {
                const v = avtVerdict(x)
                return (
                  <tr key={x.stock_item_id}>
                    <td className="px-3 py-2">
                      <p className="font-medium">{x.item_name}</p>
                      <p className="text-xs text-muted-foreground">{x.unit}{x.status !== 'ok' ? ` · ${FR.avt.status[x.status]}` : ''}</p>
                    </td>
                    <td className={td}>{fmtQty(x.opening_qty)}</td>
                    <td className={td}>{fmtQty(x.transfer_in_qty)}</td>
                    <td className={td}>{fmtQty(x.produced_qty)}</td>
                    <td className={td}>{fmtQty(x.sold_qty)}</td>
                    <td className={td}>{fmtQty(x.used_in_production_qty)}</td>
                    <td className={td}>{r.waste_tracked ? fmtQty(x.waste_qty) : FR.avt.notRecorded}</td>
                    <td className={td}>{fmtQty(avtOtherNet(x))}</td>
                    <td className={td}>{fmtQty(x.expected_closing_qty)}</td>
                    <td className={td}>{fmtQty(x.closing_qty)}</td>
                    <td className={cn(td, v.kind === 'missing' && 'font-semibold text-danger-fg')}>
                      {v.kind === 'none' ? '—' : v.kind === 'match' ? '0' : `${fmtQty(v.qty)} ${FR.avt[v.kind]}`}
                      {x.unexplained_pct != null && v.kind !== 'match' ? <span className="block text-xs text-muted-foreground">{fmtPct(x.unexplained_pct)}</span> : null}
                    </td>
                    <td className={td}>{x.unexplained_value != null ? formatCurrency(x.unexplained_value) : '—'}</td>
                  </tr>
                )
              })}
            </Table>
          </div>
        </details>
      )}
    </>
  )
}

// ── Store requests: fill rate and timing ────────────────────────────────────

function FillTable({ title, rows }: { title: string; rows: FillRateRow[] }) {
  return (
    <div className="space-y-2">
      <h3 className="font-semibold">{title}</h3>
      <Table cols={FR.requests.cols}>
        {rows.map((x) => (
          <tr key={x.key}>
            <td className="px-3 py-2 font-medium">{x.name}{x.unit ? <span className="text-xs text-muted-foreground"> · {x.unit}</span> : null}</td>
            <td className={td}>{fmtQty(x.requested_qty)}</td>
            <td className={td}>{fmtQty(x.approved_qty)}</td>
            <td className={td}>{fmtQty(x.issued_qty)}</td>
            <td className={td}>{fmtQty(x.received_qty)}</td>
            <td className={cn(td, x.fill_pct != null && x.fill_pct < 90 && 'font-semibold text-danger-fg')}>{fmtPct(x.fill_pct)}</td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

// Per place the units mix, so no quantity columns: each line's share, averaged.
function PlaceFillTable({ rows }: { rows: FillRateRow[] }) {
  return (
    <div className="space-y-2">
      <h3 className="font-semibold">{FR.requests.byPlace}</h3>
      <p className="text-sm text-muted-foreground">{FR.requests.placeHint}</p>
      <Table cols={FR.requests.placeCols}>
        {rows.map((x) => (
          <tr key={x.key}>
            <td className="px-3 py-2 font-medium">{x.name}</td>
            <td className={td}>{x.requests}</td>
            <td className={td}>{x.lines ?? '—'}</td>
            <td className={td}>{fmtPct(x.issue_pct)}</td>
            <td className={cn(td, x.fill_pct != null && x.fill_pct < 90 && 'font-semibold text-danger-fg')}>{fmtPct(x.fill_pct)}</td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

function RequestsSection({ range }: { range: UseReportRange }) {
  const q = useQuery({
    queryKey: ['reports-v2', 'kitchen-flow', 'fill-rate', range.fromISO, range.toISO],
    queryFn: () => apiClient.getFlowFillRate(range.fromISO, range.toISO),
    staleTime: 30_000,
  })
  const r = q.data?.data
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{FR.requests.title}</h2>
        {r && <p className="text-sm text-muted-foreground">{FR.requests.requests(r.requests)}</p>}
      </div>
      <State loading={q.isLoading} error={q.isError} empty={!!r && r.requests === 0} />
      {r && r.requests > 0 && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {r.timing.map((t) => (
              <Stat key={t.step} label={FR.requests.steps[t.step]} value={formatMinutes(t.median_minutes)}
                sub={`${FR.requests.p90}: ${formatMinutes(t.p90_minutes)}`} />
            ))}
          </div>
          <PlaceFillTable rows={r.by_location} />
          <FillTable title={FR.requests.byItem} rows={r.by_item} />
        </>
      )}
    </div>
  )
}

// ── Lost in transit ─────────────────────────────────────────────────────────

function TransitSection({ range }: { range: UseReportRange }) {
  const { formatCurrency } = useCurrency()
  const q = useQuery({
    queryKey: ['reports-v2', 'kitchen-flow', 'transit', range.fromISO, range.toISO],
    queryFn: () => apiClient.getFlowTransitVariance(range.fromISO, range.toISO),
    staleTime: 30_000,
  })
  const r = q.data?.data
  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">{FR.transit.title}</h2>
      <State loading={q.isLoading} error={q.isError} empty={!!r && r.lines.length === 0} />
      {r && r.lines.length > 0 && (
        <>
          <Stat label={FR.transit.total} value={formatCurrency(r.total_value)} tone={r.total_value > 0 ? 'bad' : undefined} />
          <div className="space-y-2">
            <h3 className="font-semibold">{FR.transit.byPerson}</h3>
            <ul className="divide-y rounded-lg border bg-card">
              {r.by_person.map((p) => (
                <li key={`${p.issued_by ?? ''}|${p.received_by ?? ''}`} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span className="font-medium">{FR.transit.pair(p.issued_by ?? FR.transit.nobody, p.received_by ?? FR.transit.nobody)}</span>
                  <span className="tabular-nums text-muted-foreground">{FR.transit.cases(p.lines)} · {formatCurrency(p.value)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold">{FR.transit.lines}</h3>
            <Table cols={FR.transit.cols}>
              {r.lines.map((x) => (
                <tr key={x.requisition_id + x.item_name}>
                  <td className="px-3 py-2 font-medium">{x.req_no}</td>
                  <td className="px-3 py-2">{x.item_name}</td>
                  <td className={td}>{fmtQty(x.short_qty)} {x.unit}{x.still_in_transit ? <span className="block text-xs text-warning-fg">{FR.transit.stillOut}</span> : null}</td>
                  <td className={td}>{fmtQty(x.written_off_qty)}</td>
                  <td className={td}>{formatCurrency(x.value)}</td>
                  <td className={td}>{x.issued_by ?? '—'}</td>
                  <td className={td}>{x.received_by ?? '—'}</td>
                  <td className={td}>{x.authorised_by ?? '—'}</td>
                  <td className={td}>{x.reason ?? ''}</td>
                </tr>
              ))}
            </Table>
          </div>
        </>
      )}
    </div>
  )
}

// ── Cooking yield ───────────────────────────────────────────────────────────

function YieldTable({ title, rows }: { title: string; rows: YieldRow[] }) {
  return (
    <div className="space-y-2">
      <h3 className="font-semibold">{title}</h3>
      <Table cols={FR.cooking.cols}>
        {rows.map((x) => (
          <tr key={x.key || x.name}>
            <td className="px-3 py-2 font-medium">{x.name}{x.unit ? <span className="text-xs text-muted-foreground"> · {x.unit}</span> : null}</td>
            <td className={td}>{x.batches}</td>
            <td className={td}>{fmtQty(x.planned_qty)}</td>
            <td className={td}>{fmtQty(x.actual_qty)}</td>
            <td className={cn(td, x.variance_qty < 0 && 'font-semibold text-danger-fg')}>{fmtQty(x.variance_qty)}</td>
            <td className={td}>{fmtPct(x.variance_pct)}</td>
            <td className={td}>{x.batches_under_yield}</td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

function CookingSection({ range }: { range: UseReportRange }) {
  const q = useQuery({
    queryKey: ['reports-v2', 'kitchen-flow', 'yield', range.fromISO, range.toISO],
    queryFn: () => apiClient.getFlowYield(range.fromISO, range.toISO),
    staleTime: 30_000,
  })
  const r = q.data?.data
  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">{FR.cooking.title}</h2>
      <State loading={q.isLoading} error={q.isError} empty={!!r && r.by_item.length === 0} />
      {r && r.by_item.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          <YieldTable title={FR.cooking.byItem} rows={r.by_item} />
          <YieldTable title={FR.cooking.byCook} rows={r.by_cook} />
        </div>
      )}
    </div>
  )
}

// ── Stock by place + expiry watch ───────────────────────────────────────────

function StockSection() {
  const { formatCurrency } = useCurrency()
  const q = useQuery({ queryKey: ['reports-v2', 'kitchen-flow', 'stock'], queryFn: () => apiClient.getFlowStockByLocation(), staleTime: 30_000 })
  const r = q.data?.data
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{FR.stock.title}</h2>
        {r && <p className="text-sm text-muted-foreground">{FR.stock.asOf(r.as_of)}</p>}
      </div>
      <State loading={q.isLoading} error={q.isError} empty={!!r && r.locations.length === 0} />
      {r && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {r.locations.map((l) => (
              <Stat key={l.location.id} label={l.location.name} value={formatCurrency(l.value)}
                sub={[FR.stock.items(l.items), FR.stock.slow(l.slow_count), FR.stock.dead(l.dead_count)].join(' · ')} />
            ))}
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold">{FR.stock.expiring}</h3>
            {r.expiring.length === 0 ? <p className="text-sm text-muted-foreground">{FR.stock.noExpiry}</p> : (
              <Table cols={FR.stock.expiryCols}>
                {r.expiring.map((x, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">{x.location}</td>
                    <td className={td}>{x.item_name}</td>
                    <td className={td}>{x.lot_no ?? '—'}</td>
                    <td className={td}>{x.expiry_date}</td>
                    <td className={cn(td, x.days_left <= 0 && 'font-semibold text-danger-fg')}>{x.days_left}</td>
                    <td className={td}>{fmtQty(x.qty)} {x.unit}</td>
                    <td className={td}>{formatCurrency(x.value)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
          {r.locations.map((l) => (
            <details key={l.location.id} className="rounded-lg border bg-card">
              <summary className="cursor-pointer px-4 py-3 font-semibold">{l.location.name} · {FR.stock.items(l.items)}</summary>
              <Table cols={FR.stock.cols} className="m-3">
                {l.rows.map((x) => (
                  <tr key={x.stock_item_id}>
                    <td className="px-3 py-2">{x.item_name}</td>
                    <td className={td}>{fmtQty(x.qty)} {x.unit}</td>
                    <td className={td}>{formatCurrency(x.value)}</td>
                    <td className={td}>{fmtQty(x.avg_daily_use)}</td>
                    <td className={td}>{x.days_on_hand != null ? fmtQty(x.days_on_hand) : '—'}</td>
                    <td className={cn(td, x.status !== 'ok' && 'font-medium text-warning-fg')}>{FR.stock.status[x.status]}</td>
                  </tr>
                ))}
              </Table>
            </details>
          ))}
        </>
      )}
    </div>
  )
}

// ── Buying: price variance and suppliers ────────────────────────────────────

function BuyingSection({ range }: { range: UseReportRange }) {
  const { formatCurrency } = useCurrency()
  const q = useQuery({
    queryKey: ['reports-v2', 'kitchen-flow', 'purchasing', range.fromISO, range.toISO],
    queryFn: () => apiClient.getFlowPurchasing(range.fromISO, range.toISO),
    staleTime: 30_000,
  })
  const r = q.data?.data
  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">{FR.buying.title}</h2>
      <State loading={q.isLoading} error={q.isError} empty={!!r && r.price_variance.length === 0 && r.vendors.length === 0} />
      {r && (r.price_variance.length > 0 || r.vendors.length > 0) && (
        <>
          <Stat label={FR.buying.varianceTotal} value={formatCurrency(r.variance_value)} tone={r.variance_value > 0 ? 'bad' : r.variance_value < 0 ? 'good' : undefined} />
          <div className="space-y-2">
            <h3 className="font-semibold">{FR.buying.vendorTitle}</h3>
            <Table cols={FR.buying.vendorCols}>
              {r.vendors.map((v) => (
                <tr key={v.supplier_id}>
                  <td className="px-3 py-2 font-medium">{v.supplier}</td>
                  <td className={td}>{v.orders}</td>
                  <td className={td}>{fmtPct(v.fill_pct)}</td>
                  <td className={td}>{FR.buying.onTime(v.orders_on_time, v.orders_with_dates)} · {fmtPct(v.on_time_pct)}</td>
                  <td className={cn(td, v.orders_overdue > 0 && 'font-semibold text-danger-fg')}>{v.orders_overdue}</td>
                </tr>
              ))}
            </Table>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold">{FR.buying.priceTitle}</h3>
            <Table cols={FR.buying.priceCols}>
              {r.price_variance.map((x) => (
                <tr key={x.stock_item_id}>
                  <td className="px-3 py-2 font-medium">{x.item_name}</td>
                  <td className={td}>{fmtQty(x.qty)} {x.unit}</td>
                  <td className={td}>{x.baseline_unit_cost != null ? formatCurrency(x.baseline_unit_cost) : '—'}{x.baseline_from_window ? <span className="block text-xs text-muted-foreground">{FR.buying.firstPrice}</span> : null}</td>
                  <td className={td}>{formatCurrency(x.avg_unit_cost)}</td>
                  <td className={td}>{formatCurrency(x.last_unit_cost)}</td>
                  <td className={cn(td, (x.change_pct ?? 0) > 0 && 'font-semibold text-danger-fg')}>{fmtPct(x.change_pct)}</td>
                  <td className={td}>{x.variance_value != null ? formatCurrency(x.variance_value) : '—'}</td>
                  <td className={td}>{x.last_supplier ?? '—'}</td>
                </tr>
              ))}
            </Table>
          </div>
        </>
      )}
    </div>
  )
}
